import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '../../../environments/environment';
import { CCE, CCECmd, CCECmdVolume, CCEResult, CCEVolume, ChartAxisDefault, ChartPoint, Phase, SlopeAInterceptA } from '../models/cce';
import { APIResponse } from '../../models/APIResponse';
import { Chart } from 'chart.js/auto';
import { StatusParams } from '../../msst/models/msst';
import { BPTable, SummaryOfBPTable } from '../../models/lab-test';
import { NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { chartConstant } from '../../common/experiment-constants';
import { apiV1 } from '../../common/texts-constants';

@Injectable({
  providedIn: 'root'
})
export class CCEService implements OnDestroy {
  private http = inject(HttpClient);
  router = inject(Router);
  private routerSubscription: Subscription;


  private apiBaseUrl = environment.apiUrl+apiV1;
  private volumeDatasource = new BehaviorSubject<CCEVolume[]>([]);
  currentVolumeData = this.volumeDatasource.asObservable();
  private cmdDatasource = new BehaviorSubject<CCECmd[]>([]);
  currentCmdData = this.cmdDatasource.asObservable();
  public statusChange$ = new BehaviorSubject<StatusParams>({ statusId: 0, statusName: '' });

  constructor() {
    const router = this.router;

    this.routerSubscription = router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        //handle navigation end
      }
    });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  // Api call to save CCE test details 
  SaveCCE(cce: CCE) {
    return this.http.post<APIResponse<CCE>>(this.apiBaseUrl + 'CCE/SaveCCE', cce);
  }
  PartialSaveAndUpdateCCE(cce: CCECmdVolume) {
    return this.http.post<APIResponse<CCECmdVolume>>(this.apiBaseUrl + 'CCE/PartialSaveAndUpdateCCE', cce);
  }
  // Api call to Get CCE test details
  GetCCE(testRepeatId: number) {
    return this.http.get<APIResponse<CCE>>(this.apiBaseUrl + 'CCE/GetCCE?testRepeatId=' + testRepeatId);
  }

  // Api call to Get CCE test details
  GetCCEReport(testRepeatId: number) {
    return this.http.get<APIResponse<CCE>>(this.apiBaseUrl + 'CCE/GetCCEForFinalReport?testRepeatId=' + testRepeatId);
  }
  // Api call to Update CCE test details 
  UpdateCCE(cce: CCE) {
    return this.http.post<APIResponse<CCE>>(this.apiBaseUrl + 'CCE/UpdateCCE', cce);
  }

  // Api call to Delete CCE test details 
  DeleteCCE(cceId: string) {
    return this.http.delete(this.apiBaseUrl + 'CCE/DeleteCCE/' + cceId);
  }

  // Api call to Get LiquidVolume Calculation 
  GetLiquidVolumeCalculation(volumeInput: CCEVolume[]) {
    return this.http.post<APIResponse<CCEVolume[]>>(this.apiBaseUrl + 'CCE/LiquidVolumeCalculation', volumeInput)
  }

  // Round number to decimal places 
  RoundTo(num: number, places: number) {
    const factor = 10 ** places;
    return Math.round(num * factor) / factor;
  }

  CalculateLVMassDensityMinAndMax(bpTable: BPTable[]): { lVMassMin: number; lVMassMax: number; sliceData: BPTable[] } {
    const findIndex = bpTable.findIndex((x: BPTable) => x.pointName === 'Sat P');
    const lastIndex = findIndex >= NUMBERS_CONSTANTS.zero ? findIndex + NUMBERS_CONSTANTS.one : bpTable.length;
    const sliceData: BPTable[] = bpTable.slice(NUMBERS_CONSTANTS.zero, lastIndex);

    if (sliceData.length === NUMBERS_CONSTANTS.zero) {
      return { lVMassMin: NUMBERS_CONSTANTS.zero, lVMassMax: NUMBERS_CONSTANTS.zero, sliceData: [] };
    }

    const massDensityValues = sliceData.map(x => x.lVMassDens ?? NUMBERS_CONSTANTS.zero);

    let lVMassMax = massDensityValues.reduce((a, b) => (a > b ? a : b));
    let lVMassMin = massDensityValues.reduce((a, b) => (a < b ? a : b));

    lVMassMin = Math.floor(lVMassMin * NUMBERS_CONSTANTS.hundred) / NUMBERS_CONSTANTS.hundred;
    lVMassMax = Math.ceil(lVMassMax * NUMBERS_CONSTANTS.hundred) / NUMBERS_CONSTANTS.hundred;
    return { lVMassMin, lVMassMax, sliceData };
  }

  // Map CCE result oil data to bubblepoint report rows (cellVolume of 0 displayed as null)
  MapResultOilDataToReportRows(resultOilData: CCEResult[]): CCEResult[] {
    return resultOilData.map((obj: CCEResult) => ({
      pressure: obj.pressure,
      pointName: obj.pointName,
      vVSat: obj.vVSat,
      lVMassDens: obj.lVMassDens,
      compressibility: obj.compressibility,
      liquidVolume: obj.liquidVolume,
      cellVolume: obj.cellVolume === NUMBERS_CONSTANTS.zero ? null : obj.cellVolume
    }));
  }

  // Map CCE result oil data to bubblepoint PDF report rows (compressibility defaults to 0)
  MapResultOilDataForReport(resultOilData: CCEResult[]): CCEResult[] {
    return resultOilData.map((obj: CCEResult) => ({
      pressure: obj.pressure,
      pointName: obj.pointName,
      vVSat: obj.vVSat,
      lVMassDens: obj.lVMassDens,
      compressibility: obj.compressibility ?? NUMBERS_CONSTANTS.zero,
      liquidVolume: obj.liquidVolume,
      cellVolume: obj.cellVolume
    }));
  }

  // Build V/Vsat vs pressure and liquid-volume vs pressure series from bubblepoint report rows
  BuildPressureSeriesFromReportData(reportListData: CCEResult[]): { objArray: ChartPoint[]; objNewArray: ChartPoint[] } {
    const objArray: ChartPoint[] = [];
    const objNewArray: ChartPoint[] = [];
    for (let n = 0; reportListData.length > n; n++) {
      objArray.push({ x: reportListData[n].pressure, y: reportListData[n].vVSat });
      objNewArray.push({ x: reportListData[n].pressure, y: reportListData[n].liquidVolume });
    }
    return { objArray, objNewArray };
  }

  // Filter bubblepoint report rows to a Pressure V/Vsat series within the given axis bounds
  FilterPressureVVSatSeries(reportListData: CCEResult[], xMin: number, xMax: number, yMin: number, yMax: number): ChartPoint[] {
    return reportListData
      .filter(item =>
        item.pressure >= xMin &&
        item.pressure <= xMax &&
        item.vVSat >= yMin &&
        item.vVSat <= yMax
      )
      .map(item => ({
        x: item.pressure,
        y: item.vVSat
      }));
  }

  // Filter bubblepoint report rows to a Pressure Liquid-Volume series within the given axis bounds
  FilterPressureVolumeSeries(reportListData: CCEResult[], xMin: number, xMax: number, yMin: number, yMax: number): ChartPoint[] {
    return reportListData
      .filter(item =>
        item.pressure >= xMin &&
        item.pressure <= xMax &&
        item.liquidVolume >= yMin &&
        item.liquidVolume <= yMax
      )
      .map(item => ({
        x: item.pressure,
        y: item.liquidVolume
      }));
  }

  // Build V/Vsat vs pressure series from BP table rows
  BuildVSatSeries(bpTable: BPTable[]): ChartPoint[] {
    return bpTable.map((x: BPTable) => ({
      x: x.pressure,
      y: x.vVSat
    }));
  }

  // Filter BP table rows to a V/Vsat series within the given axis bounds
  FilterVSatSeries(bpTable: BPTable[], xMin: number, xMax: number, yMin: number, yMax: number): ChartPoint[] {
    return bpTable
      .filter(item =>
        item.pressure >= xMin &&
        item.pressure <= xMax &&
        item.vVSat >= yMin &&
        item.vVSat <= yMax
      )
      .map(item => ({
        x: item.pressure,
        y: item.vVSat
      }));
  }

  // Build L/V mass-density vs pressure series from (sliced) BP table rows
  BuildLVMassSeries(bpTable: BPTable[]): ChartPoint[] {
    return bpTable.map((x: BPTable) => ({
      x: x.pressure,
      y: x.lVMassDens ?? NUMBERS_CONSTANTS.zero
    }));
  }

  // Filter BP table rows to a L/V mass-density series within the given axis bounds
  FilterLVMassSeries(bpTable: BPTable[], xMin: number, xMax: number, yMin: number, yMax: number): ChartPoint[] {
    return bpTable
      .filter(item =>
        item.pressure >= xMin &&
        item.pressure <= xMax &&
        item.lVMassDens! >= yMin &&
        item.lVMassDens! <= yMax
      )
      .map(item => ({
        x: item.pressure,
        y: item.lVMassDens!
      }));
  }

  // Build isothermal compressibility vs pressure series from BP table rows
  BuildIsothermalSeries(bpTable: BPTable[]): ChartPoint[] {
    return bpTable.map((x: BPTable) => ({
      x: x.pressure,
      y: x.compressibility ?? NUMBERS_CONSTANTS.zero
    }));
  }

  // Filter BP table rows to an isothermal compressibility series within the given axis bounds
  FilterIsothermalSeries(bpTable: BPTable[], xMin: number, xMax: number, yMin: number, yMax: number): ChartPoint[] {
    return bpTable
      .filter(item =>
        item.pressure >= xMin &&
        item.pressure <= xMax &&
        item.compressibility != null &&
        item.compressibility >= yMin &&
        item.compressibility <= yMax
      )
      .map(item => ({
        x: item.pressure,
        y: item.compressibility as number
      }));
  }

  // Build liquid-volume vs pressure series from BP table rows
  BuildLiquidVolumeSeries(bpTable: BPTable[]): ChartPoint[] {
    return bpTable.map((x: BPTable) => ({
      x: x.pressure,
      y: x.liquidVolume!
    }));
  }

  // Filter BP table rows to a liquid-volume series within the given axis bounds
  FilterLiquidVolumeSeries(bpTable: BPTable[], xMin: number, xMax: number, yMin: number, yMax: number): ChartPoint[] {
    return bpTable
      .filter(item =>
        item.pressure >= xMin &&
        item.pressure <= xMax &&
        item.liquidVolume! >= yMin &&
        item.liquidVolume! <= yMax
      )
      .map(item => ({
        x: item.pressure,
        y: item.liquidVolume!
      }));
  }
  CreateChangeSnapshot(value: unknown): string {
    return JSON.stringify(this.NormalizeForComparison(value));
  }

  private NormalizeForComparison(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(item => this.NormalizeForComparison(item));
    }

    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [
        key,
        this.NormalizeForComparison(item)
      ]));
    }

    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }

    return value;
  }

  // Find next number in arithmetic sequence
  FindNextNumber(sequence: number[]): number {
    if (sequence.length < 2) {
      throw new Error('Sequence must have at least two numbers');
    }
    const difference = sequence[1] - sequence[0];
    return sequence[sequence.length - 1] + difference;
  }

  // Create Scatter Chart for V/Vsat, Isothermal Compressibility and Bubble Point Determination Check
  CreateScatterChart(ctx: CanvasRenderingContext2D, originalVSatData: {x: number; y:number}[],
    DataSetlabels: string, PlotTitle: string, xAxisTitle: string, yAxisTitle: string): Chart {
    return new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: DataSetlabels,
            data: originalVSatData,
            borderColor: '#5B9BD5',
            backgroundColor: '#5B9BD5',
            showLine: false,
            fill: false,
            borderWidth: 1,
            pointStyle: 'rectRot',
            pointRadius: 6,
          }
        ],
      },
      options: {
        animation: false,
        plugins: {
          title: {
            display: true,
            text: PlotTitle,
            font: {
              size: 40
            }
          },
          legend: {
            labels: {
              usePointStyle: true,
              font: {
                size: 30
              }
            },
            position: 'right'
          },
          datalabels: {
            display: false
          }
        },
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: 1
            },
            title: {
              display: true,
              font: {
                size: 30
              },
              text: xAxisTitle
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 20
              },
              color: '#000000'
            },
            grid: {
              display: false
            }
          },
          y: {
            border: {
              display: true,
              color: '#000000',
              width: 1
            },
            title: {
              display: true,
              font: {
                size: 30
              },
              text: yAxisTitle
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 20
              },
            },
            grid: {
              display: false
            }
          },

        },
      },
    });
  }

  // Create Scatter Chart For CCE Calculation
  CreateScatterChartForCCECalc(ctx: string, originalVSatData: {x: number; y:number}[],
    DataSetlabels: string, PlotTitle: string, xAxisTitle: string, yAxisTitle: string): Chart {
    return new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: DataSetlabels,
            data: originalVSatData,
            borderColor: '#5B9BD5',
            backgroundColor: '#5B9BD5',
            showLine: false,
            fill: false,
            borderWidth: 1,
            pointStyle: 'rectRot',
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: PlotTitle
          },
          legend: {
            labels: {
              usePointStyle: true,
            },
            position: 'right',
          },
          datalabels: {
            display: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: true
              },
              mode: 'xy'
            },
            pan: {
              enabled: true,
              mode: 'xy'
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: xAxisTitle,
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
              },
              color: '#000000',
            },
            grid: {
              display: false,
            },
          },
          y: {
            title: {
              display: true,
              text: yAxisTitle,
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
              },
            },
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }

  // Create Scatter Chart For CCE Calculation With Min Max Report
  CreateScatterChartForCCECalcWithMinMaxReport(ctx: CanvasRenderingContext2D, originalVSatData: {x: number; y:number}[],
    DataSetlabels: string, PlotTitle: string, xAxisTitle: string, yAxisTitle: string, ymin: number, ymax: number): Chart {
    return new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: DataSetlabels,
            data: originalVSatData,
            borderColor: '#5B9BD5',
            backgroundColor: '#5B9BD5',
            showLine: false,
            fill: false,
            borderWidth: 1,
            pointStyle: 'rectRot',
            pointRadius: 6,
          },
        ],
      },
      options: {
        animation: false,
        plugins: {
          title: {
            display: true,
            text: PlotTitle,
            font: {
              size: 40
            }
          },
          legend: {
            labels: {
              usePointStyle: true,
              font: {
                size: 30
              }
            },
            position: 'right',
          },
          datalabels: {
            display: false
          }
        },
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: 1
            },
            title: {
              display: true,
              text: xAxisTitle,
              font: {
                size: 30
              },
            },
            beginAtZero: true,
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 20
              },
              color: '#000000',
            },
            grid: {
              display: false,
            },
          },
          y: {
            border: {
              display: true,
              color: '#000000',
              width: 1
            },
            title: {
              display: true,
              text: yAxisTitle,
              font: {
                size: 30
              },
            },
            min: Math.floor(ymin * 100) / 100,
            max: Math.ceil(ymax * 100) / 100,
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 20
              },
            },
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }

  // Create Scatter Chart For CCE Calculation With Min Max
  CreateScatterChartForCCECalcWithMinMax(ctx: string, originalVSatData: {x: number; y:number}[],
    DataSetlabels: string, PlotTitle: string, xAxisTitle: string, yAxisTitle: string, ymin: number, ymax: number): Chart {
    return new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: DataSetlabels,
            data: originalVSatData,
            borderColor: '#5B9BD5',
            backgroundColor: '#5B9BD5',
            showLine: false,
            fill: false,
            borderWidth: 1,
            pointStyle: 'rectRot',
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: PlotTitle
          },
          legend: {
            labels: {
              usePointStyle: true,
            },
            position: 'right',
          },
          datalabels: {
            display: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: true
              },
              mode: 'xy'
            },
            pan: {
              enabled: true,
              mode: 'xy'
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: xAxisTitle,
            },
            beginAtZero: true,
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
              },
              color: '#000000',
            },
            grid: {
              display: false,
            },
          },
          y: {
            title: {
              display: true,
              text: yAxisTitle,
            },
            min: ymin,     // Math.floor(ymin * 100) / 100 - Calculated in CalculateL_VMassDensityMinAndMax()
            max: ymax,    //Math.ceil(ymax * 100) / 100 - Calculated in CalculateL_VMassDensityMinAndMax()
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
              },
            },
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }

  // Create graphs for Plots Oil
  CreateChartPlotsOilReport(bubbletableData: BPTable[], ctxPO1: CanvasRenderingContext2D, ctxPO2: CanvasRenderingContext2D) {
    const objArray = [{}];
    const objNewArray = [{}];
    for (let n = 0; bubbletableData.length > n; n++) {
      objArray.push({ x: bubbletableData[n].pressure, y: bubbletableData[n].vVSat })
      objNewArray.push({ x: bubbletableData[n].pressure, y: bubbletableData[n].liquidVolume })
    }
    /*Start Min&Max calculations for V/Vsat and Liquid Volume*/
    let minVsatY = Number.MAX_VALUE;
    let maxVsatY = Number.MIN_VALUE;
    let minLiquidVolumeY = Number.MAX_VALUE;
    let maxLiquidVolumeY = Number.MIN_VALUE;
    let maxPressureX = Number.MIN_VALUE;

    for (const item of bubbletableData) {
      if (item.vVSat !== undefined) {
        minVsatY = Math.min(minVsatY, item.vVSat);
        maxVsatY = Math.max(maxVsatY, item.vVSat);
      }
      if (item.liquidVolume !== undefined) {
        minLiquidVolumeY = Math.min(minLiquidVolumeY, item.liquidVolume);
        maxLiquidVolumeY = Math.max(maxLiquidVolumeY, item.liquidVolume);
      }
      if (item.pressure !== undefined) {
        maxPressureX = Math.max(maxPressureX, item.pressure);
      }
    }
    const yMin = Math.floor((minVsatY - 0.1) * 10) / 10;
    const yMax = Math.ceil((maxVsatY + 0.1) * 10) / 10;
    const xMax = maxPressureX>=13900 ? 16000 : 14000;
    const yMaxLiquidVolume = (Math.ceil(maxLiquidVolumeY / 10) * 10)+10;
    const yMinLiquidVolume = minLiquidVolumeY >= 1 ? 0 : minLiquidVolumeY<0? Math.ceil(minLiquidVolumeY)-10: Math.floor(minLiquidVolumeY)-10;
    /*End Min&Max calculations for V/Vsat and Liquid Volume*/
    //For Pressure V/Vsat chart
    const pressureV_VsatChart = new Chart(ctxPO1, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objArray,
            borderColor: '#000000ff',
            backgroundColor: '#c55a11',
            showLine: false,
            fill: false,
            borderWidth: 3,
            pointStyle: 'circle',
            pointRadius: 11,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 30
            },
            color: '#000000'
          },
          legend: {
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: NUMBERS_CONSTANTS.three
            },
            title: {
              display: true,
              text: 'Pressure (psia)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 35
              },
              color: '#000000',
              stepSize: 2000,
              callback: (value) => value.toString()
            },
            min: 0,
            max: xMax,
            grid: {
              display: false
            },

          },
          y: {
            title: {
              display: true,
              text: 'V/Vsat',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: NUMBERS_CONSTANTS.three
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 35
              },
              stepSize: 0.1
            },
            min: yMin,
            max: yMax,
            grid: {
              display: false
            },
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    //For Pressure Liquid volume chart
    const pressureVolumeChart = new Chart(ctxPO2, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objNewArray,
            borderColor: '#000000ff',
            backgroundColor: '#c55a11',
            showLine: false,
            fill: false,
            borderWidth: 3,
            pointStyle: 'circle',
            pointRadius: NUMBERS_CONSTANTS.eleven,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 40,
              weight: 'bold'
            },
            color: '#000000'
          },
          legend: {
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: 3
            },
            title: {
              display: true,
              text: 'Pressure (psia)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: NUMBERS_CONSTANTS.thirtyFive
              },
              color: '#000000',
              stepSize: 2000,
              callback: (value) => value.toString()
            },
            min: 0,
            max: xMax,
            grid: {
              display: false
            },

          },
          y: {
            title: {
              display: true,
              text: 'Liquid Vol (%)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: 3
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: NUMBERS_CONSTANTS.thirtyFive
              },
              stepSize: 10
            },
            min: yMinLiquidVolume,
            max: yMaxLiquidVolume,
            grid: {
              display: false
            },
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    return [pressureV_VsatChart, pressureVolumeChart];
  }
  // Calculate Trendline of Buuble Point Determination Check graph
  CalculateTrendline(
    data: { x: number; y: number }[],
    forward: number,
    backward: number
  ) {
    const n = data.length;
    const sumX = data.reduce((sum, point) => sum + point.x, 0);
    const sumY = data.reduce((sum, point) => sum + point.y, 0);
    const sumXY = data.reduce((sum, point) => sum + point.x * point.y, 0);
    const sumX2 = data.reduce((sum, point) => sum + point.x * point.x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    const trendlineDataOne = [];
    const trendlineLabelsOne = [];

    for (let i = -backward; i < n + forward; i++) {
      const x = data[0].x + i;
      trendlineDataOne.push({ x, y: slope * x + intercept });
      trendlineLabelsOne.push(x);
    }

    return { trendlineDataOne, trendlineLabelsOne };
  }

  // Calculate Trendline Two of Buuble Point Determination Check graph
  CalculateTrendlineTwo(
    data: { x: number; y: number }[],
    forward: number,
    backward: number
  ) {
    const n = data.length;
    const sumX = data.reduce((sum, point) => sum + point.x, 0);
    const sumY = data.reduce((sum, point) => sum + point.y, 0);
    const sumXY = data.reduce((sum, point) => sum + point.x * point.y, 0);
    const sumX2 = data.reduce((sum, point) => sum + point.x * point.x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    const trendlineDataTwo = [];
    const trendlineLabelsTwo = [];

    for (let i = -backward; i < n + forward; i++) {
      const x = data[0].x + i;
      trendlineDataTwo.push({ x, y: slope * x + intercept });
      trendlineLabelsTwo.push(x);
    }

    return { trendlineDataTwo, trendlineLabelsTwo };
  }

  // Calculate the two phase series, calc-bubble-point point, and axis defaults for the Bubble Point Determination Check graph
  CalculateBubblePointChartData(
    phaseOne: Phase[],
    phaseTwo: Phase[],
    bubblePointPressure: number,
    slopeAInterceptA: SlopeAInterceptA,
    trendAbove: number,
    trendBelow: number
  ): {
    p1Data: ChartPoint[];
    p2Data: ChartPoint[];
    bubblePointSeries: ChartPoint[];
    xMin: number;
    xMax: number;
    xStep: number;
    yMin: number;
    yMax: number;
    yStep: number;
  } {
    const originalDataone = phaseOne
      .map((x: Phase) => {
        return {
          x: x.p1Cor,
          y: x.vCor,
        };
      })
      .reverse();
    const originalDatatwo = phaseTwo.map((x: Phase) => {
      return {
        x: x.p1Cor,
        y: x.vCor,
      };
    });
    const bubblePointSeries = [
      {
        x: bubblePointPressure,
        y:
          (slopeAInterceptA.slope ?? 0) * bubblePointPressure +
          (slopeAInterceptA.intercept ?? 0),
      },
    ];
    const { trendlineDataOne } = this.CalculateTrendline(
      originalDataone,
      0,
      trendAbove
    );
    const { trendlineDataTwo } = this.CalculateTrendlineTwo(
      originalDatatwo,
      trendBelow,
      0
    );

    const p1Data = [trendlineDataOne[NUMBERS_CONSTANTS.zero]].concat(originalDataone);
    const p2Data = [trendlineDataTwo[trendlineDataTwo.length - NUMBERS_CONSTANTS.one]].concat(
      originalDatatwo
    );
    const p1DifferenceLast =
      p1Data[p1Data.length - NUMBERS_CONSTANTS.one].y - p1Data[p1Data.length - NUMBERS_CONSTANTS.two].y;
    const p1nextNumberMin = p1Data[p1Data.length - NUMBERS_CONSTANTS.one].y + p1DifferenceLast;
    const p1DifferenceFirst = p1Data[NUMBERS_CONSTANTS.one].y - p1Data[NUMBERS_CONSTANTS.zero].y;
    const p1nextNumberMax = p1Data[NUMBERS_CONSTANTS.zero].y - p1DifferenceFirst;
    const p2DifferenceLast =
      p2Data[p2Data.length - NUMBERS_CONSTANTS.one].y - p2Data[p2Data.length - NUMBERS_CONSTANTS.two].y;
    const p2nextNumberMax = p2Data[p2Data.length - 1].y + p2DifferenceLast;
    const p2DifferenceFirst = p2Data[NUMBERS_CONSTANTS.one].y - p2Data[NUMBERS_CONSTANTS.zero].y;
    const p2nextNumberMin = p2Data[NUMBERS_CONSTANTS.zero].y - p2DifferenceFirst;

    let p1ymax = p1Data
      .map(x => +x.y)
      .reduce((a, b) => (a > b ? a : b));
    let p1ymin = p1Data
      .map(x => +x.y)
      .reduce((a, b) => (a < b ? a : b));

    p1ymax =
      p1nextNumberMax > p2nextNumberMax ? p1nextNumberMax : p2nextNumberMax;
    p1ymin =
      p1nextNumberMin < p2nextNumberMin ? p1nextNumberMin : p2nextNumberMin;

    const xMin = Math.floor(Math.min(...[...p1Data, ...p2Data].map(d => d.x)) / NUMBERS_CONSTANTS.hundred) * NUMBERS_CONSTANTS.hundred;
    const xMax = Math.ceil(Math.max(...[...p1Data, ...p2Data].map(d => d.x)) / NUMBERS_CONSTANTS.hundred) * NUMBERS_CONSTANTS.hundred;
    const xStep = NUMBERS_CONSTANTS.hundred;
    const yMin = parseFloat((Math.floor(p1ymin * NUMBERS_CONSTANTS.ten) / NUMBERS_CONSTANTS.ten).toFixed(NUMBERS_CONSTANTS.two));
    const yMax = Math.round(parseFloat((Math.floor(p1ymax * NUMBERS_CONSTANTS.ten) / NUMBERS_CONSTANTS.ten).toFixed(NUMBERS_CONSTANTS.two))) + NUMBERS_CONSTANTS.zeroPointOne;
    const yStep = NUMBERS_CONSTANTS.zeroPointOne;

    return { p1Data, p2Data, bubblePointSeries, xMin, xMax, xStep, yMin, yMax, yStep };
  }

  // Update Volume Data Source
  UpdateVolumeData(newData: CCEVolume[]) {
    this.volumeDatasource.next(newData);
  }

  // Clear Volume Data Source
  ClearVolumeData() {
    this.volumeDatasource.next([]);
    this.UpdateVolumeData([]);
  }

  // Update Cmd Data Source
  UpdateCmdData(newData: CCECmd[]) {
    this.cmdDatasource.next(newData);
  }

  // Clear Cmd Data Source
  ClearCmdData() {
    this.cmdDatasource.next([]);
    this.UpdateCmdData([]);
  }

  UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
  }


  boxBorderPlugin: { id: string; beforeDraw: (chart: Chart) => void } = {
      id: 'boxBorderPlugin',
      beforeDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
  
        ctx.save();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = NUMBERS_CONSTANTS.four;
  
        // Top border
        ctx.beginPath();
        ctx.moveTo(chartArea.left, chartArea.top);
        ctx.lineTo(chartArea.right, chartArea.top);
        ctx.stroke();
  
        ctx.lineWidth = NUMBERS_CONSTANTS.three;
        // Right border
        ctx.beginPath();
        ctx.moveTo(chartArea.right, chartArea.top);
        ctx.lineTo(chartArea.right, chartArea.bottom);
        ctx.stroke();
  
        ctx.restore();
      }
    };
   axisOriginTicksPlugin: { id: string; beforeDraw: (chart: Chart) => void } = {
      id: 'axisOriginTicksPlugin',
      beforeDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const xScale = chart.scales['x'];
        const yScale = chart.scales['y'];
        const tickLength = NUMBERS_CONSTANTS.ten;
        ctx.save();
        ctx.strokeStyle = chartConstant.black;
        ctx.lineWidth = NUMBERS_CONSTANTS.two;
        // Draw ticks on X axis (inside plot area only)
        xScale.ticks.forEach(tick => {
          const x = xScale.getPixelForValue(tick.value);
          const yEnd = chart.chartArea.bottom;
  
          if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
            ctx.beginPath();
            ctx.moveTo(x, yEnd - tickLength); // bottom edge inward
            ctx.lineTo(x, yEnd);              // bottom edge
            ctx.stroke();
          }
        });
  
        // Draw ticks on Y axis (inside plot area only)
        yScale.ticks.forEach(tick => {
          const y = yScale.getPixelForValue(tick.value);
          const xStart = chart.chartArea.left;
  
          if (y >= chart.chartArea.top && y <= chart.chartArea.bottom) {
            ctx.beginPath();
            ctx.moveTo(xStart, y);              // left edge
            ctx.lineTo(xStart + tickLength, y); // left edge inward
            ctx.stroke();
          }
        });
  
        ctx.restore();
  
      }
    };
  CreateSummaryChartPlotsOilReport(bubbletableData: SummaryOfBPTable[], ctxPO1: CanvasRenderingContext2D) {
    const objArray =
      bubbletableData.map(d => ({
        x: Number(d.temp),
        y: Number(d.psat),
      }));
    const maxPSAT = Math.max(...bubbletableData.map(item => Number(item.psat) || 0));
    const roundedMaxPSAT = Math.ceil(maxPSAT / 1000) * 1000;

    const maxTemp = Math.max(...bubbletableData.map(item => Number(item.temp) || 0));
    const roundedMaxTemp = Math.ceil(maxTemp / 100) * 100;
    //For Pressure V/Vsat chart
    const pressureV_VsatChart = new Chart(ctxPO1, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objArray,
            borderColor: '#000000ff',
            backgroundColor: '#0389f6ff',
            showLine: true,
            fill: false,
            borderWidth: NUMBERS_CONSTANTS.six,
            pointStyle: 'circle',
            pointRadius: NUMBERS_CONSTANTS.nine,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 30
            },
            color: '#000000'
          },
          legend: {
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: NUMBERS_CONSTANTS.three
            },
            title: {
              display: true,
              text: 'Temperature (°F)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 35
              },
              color: '#000000',
              stepSize: NUMBERS_CONSTANTS.fifty
            },
            min: NUMBERS_CONSTANTS.fifty,
            max: roundedMaxTemp,
            grid: {
              display: false,
            },

          },
          y: {
            title: {
              display: true,
              text: 'Saturation Pressure (psia)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: NUMBERS_CONSTANTS.three
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 35
              },
              stepSize: NUMBERS_CONSTANTS.thousand,
              callback: (value) => value.toString()
            },
            min: NUMBERS_CONSTANTS.thousand,
            max: roundedMaxPSAT,
            grid: {
              display: false,
            },
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    //For Pressure Liquid volume chart

    return pressureV_VsatChart;
  }

  GetAxisDefault(chart: Chart): ChartAxisDefault {
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];

    return {
      xMin: Number(xScale.min),
      xMax: Number(xScale.max),
      xStep: this.GetTickStep(xScale.ticks),

      yMin: Number(yScale.min),
      yMax: Number(yScale.max),
      yStep: this.GetTickStep(yScale.ticks)
    };
  }

  GetTickStep(ticks: { value: number }[]): number {
    if (ticks.length >= NUMBERS_CONSTANTS.two) {
      return Number((ticks[NUMBERS_CONSTANTS.one].value - ticks[NUMBERS_CONSTANTS.zero].value).toFixed(NUMBERS_CONSTANTS.ten));
    }

    return NUMBERS_CONSTANTS.zero;
  }

  BuildTicks(min: number, max: number, step: number): { value: number }[] {
    const ticks: { value: number }[] = [];

    const decimalPlaces = step.toString().includes('.')
      ? step.toString().split('.')[NUMBERS_CONSTANTS.one].length
      : NUMBERS_CONSTANTS.zero;

    for (let value = min; value <= max; value += step) {
      ticks.push({
        value: Number(value.toFixed(decimalPlaces))
      });
    }

    const lastTick = ticks[ticks.length - NUMBERS_CONSTANTS.one];

    if (lastTick && lastTick.value !== max) {
      ticks.push({
        value: Number(max.toFixed(decimalPlaces))
      });
    }

    return ticks;
  }
}
